import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as R from 'ramda';
import {
    Card,
    Flex,
    Typography,
    Tabs,
    Tag,
    Button,
    Spin,
    theme,
} from '@app/ui';
import * as format from '../utils/formatters';
import { colors10 } from '../constants/theme';
import { Line, LineConfig } from '@ant-design/charts';
import CenterMessage from '../components/common/CenterMessage';
import stadiumLogo from '../images/StadiumOutlined.svg';
import liveTvLogo from '../images/LiveTvOutlined.svg';

const topThreeIdsMap = R.pipe(
    R.take(3),
    R.reduce((acc, opt) => R.assoc(R.prop('id', opt), true, acc), {}),
);

type TopPlacementsProp = {
    total: any;
    graph: any;
    sync: any;
    rightsholders?: any;
    type?: 'placement' | 'platform';
    currency?: string;
};

const TopLines = ({
    total,
    graph,
    sync,
    rightsholders,
    type = 'placement',
    currency,
}: TopPlacementsProp) => {
    const { t } = useTranslation('home');
    const {
        labels = [],
        data = {},
        isLoading: isLoadingGraph = false,
        hasError: hasGraphError = false,
        granularity = 'week',
    } = graph || {};
    const {
        list = [],
        isLoading: isLoadingTotal = false,
        hasError: hasTotalError = false,
    } = total || {};
    const hasError = hasTotalError || hasGraphError;
    const [activeKey, setActiveKey] = React.useState(granularity);
    const [selected, setSelected] = useState({});
    const hasSelected = Object.entries(selected).some(([, value]) => !!value);
    const [height, setHeight] = useState(0);
    const refContainer = React.useRef<HTMLDivElement>(null);

    const {
        token: { colorPrimary, colorText },
    } = theme.useToken();

    const toggleOption = (id: string) => {
        const selectedCount = Object.keys(selected).filter(
            key => selected[key],
        ).length;
        const isUnselectingLast = selected[id] && selectedCount === 1;
        if (isUnselectingLast) return;
        setSelected({
            ...selected,
            [id]: !selected[id],
        });
    };

    useEffect(() => {
        setSelected(topThreeIdsMap(list));
    }, [list]);

    const timeframesItems = React.useMemo(
        () =>
            ['day', 'week', 'month'].map(key => ({
                key,
                label: (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                        }}
                    >
                        {t(`topLines.timeframes.${key}`)}
                    </div>
                ),
            })),
        [t],
    );

    const onChange = (key: string) => {
        setActiveKey(key);
        sync(key);
    };

    const hasMultipleRightsholders =
        rightsholders?.list && Object.keys(rightsholders.list).length > 1;

    const getPlacementRightsholder = (id: string) => {
        const [rhId] = id.split('-');
        return rightsholders?.list?.[rhId];
    };

    const getPlacementRightsholderName = (item: any) => {
        return item?.tooltip || getPlacementRightsholder(item?.id)?.name || '';
    };

    const hasPlacementFallbackRightsholder = (item: any) => {
        if (type !== 'placement') {
            return false;
        }

        const hasMetadataRightsholder = Boolean(
            getPlacementRightsholder(item?.id),
        );
        return !hasMetadataRightsholder && Boolean(item?.tooltip);
    };

    const shouldShowPlacementRightsholder = (item: any) => {
        if (type !== 'placement') {
            return false;
        }

        return (
            hasMultipleRightsholders || hasPlacementFallbackRightsholder(item)
        );
    };

    const getPlacementDisplayName = (item: any) => {
        const name = item?.name || '';

        if (!shouldShowPlacementRightsholder(item)) {
            return name;
        }

        const rightsholderName = getPlacementRightsholderName(item);
        return rightsholderName ? `${name} - ${rightsholderName}` : name;
    };

    const getPlacementLogoUrl = (item: any) => {
        if (!shouldShowPlacementRightsholder(item)) {
            return null;
        }

        return (
            item?.logoUrl ||
            getPlacementRightsholder(item?.id)?.mainLogo?.url ||
            null
        );
    };

    const selectedData = React.useMemo(() => {
        return Object.entries(selected).reduce((acc, [key, value]) => {
            if (value && data[key]?.data) {
                const item = list.find(({ id }) => `${id}` === key);
                const name = item?.name;
                const customName = item ? getPlacementDisplayName(item) : name;

                const additionalData = data[key].data.map((d, index) => ({
                    Smv: d || 0,
                    Date: labels[index],
                    id: key,
                    name,
                    customName,
                }));

                acc.push(...additionalData);
            }
            return acc;
        }, []);
    }, [
        selected,
        data,
        labels,
        list,
        type,
        hasMultipleRightsholders,
        rightsholders,
    ]);

    const config: LineConfig = useMemo(() => {
        const [selectedListIndexes, selectedCustomNames] = list.reduce(
            (acc, item, index) => {
                const { id } = item;
                if (selected[id]) {
                    acc[0].push(index);
                    acc[1].push(getPlacementDisplayName(item));
                }
                return acc;
            },
            [[], []],
        );
        const selectedColors = selectedListIndexes.map(
            index => colors10[index],
        );
        return {
            data: selectedData,
            xField: d => d.Date,
            yField: 'Smv',
            colorField: 'customName',

            scale: {
                color: {
                    domain: selectedCustomNames,
                    range: selectedColors,
                },
            },
            legend: false,

            axis: {
                x: {
                    title: t('topLines.xAxisTitle', {
                        timeframe: t(`topLines.timeframes.${activeKey}`),
                    }),
                    titleFontSize: 16,
                    titleFill: '#5E6066',
                    titleStroke: 'transparent',
                    grid: true,
                    labelFontSize: 16,
                    labelFill: '#5E6066',
                    labelFontWeight: 500,
                    labelTransform: 'rotate(-30)',
                    labelSpacing: 15,
                },
                y: {
                    title: t('topLines.yAxisTitle'),
                    titleFontSize: 16,
                    titleFill: '#5E6066',
                    titleStroke: 'transparent',
                    labelFormatter: value =>
                        format.shortCurrency(value, 1, currency),
                    grid: true,
                    line: true,
                    tickLine: true,
                    labelFontSize: 16,
                    labelFill: '#5E6066',
                    labelFontWeight: 500,
                    labelOpacity: 1,
                },
            },

            tooltip: {
                channel: 'y',
                valueFormatter: v => format.shortCurrency(v, 1, currency),
            },
            loading: isLoadingGraph || isLoadingTotal,

            loadingTemplate: (
                <Flex
                    justify="center"
                    align="center"
                    style={{ height: '100%', width: '100%' }}
                >
                    <Spin size="large" />
                </Flex>
            ),
            height,
        };
    }, [
        selectedData,
        isLoadingGraph,
        isLoadingTotal,
        height,
        t,
        activeKey,
        currency,
        list,
        selected,
        type,
        hasMultipleRightsholders,
        rightsholders,
    ]);

    React.useEffect(() => {
        if (refContainer.current && list.length > 0) {
            setHeight(refContainer.current.clientHeight);
        }
    }, [list, refContainer.current]);

    return (
        <Card>
            {/* Tabs for selecting timeframe */}
            <Tabs
                activeKey={activeKey}
                items={timeframesItems}
                onChange={onChange}
                tabBarExtraContent={{
                    left: (
                        <Typography.Text
                            strong
                            style={{ paddingRight: 24, fontSize: 20 }}
                        >
                            {type === 'placement'
                                ? t('topLines.titlePlacement')
                                : t('topLines.titlePlatform')}
                        </Typography.Text>
                    ),
                }}
            />

            {/* Flex Container */}
            <Flex style={{ gap: '16px' }}>
                {/* Sidebar for Placement Buttons */}
                {!hasError && list.length > 0 && (
                    <Flex
                        style={{
                            flexDirection: 'column',
                            padding: '0 16px 16px 16px',
                            flexBasis: 240, // Fixed sidebar width
                            flexShrink: 0, // Prevent shrinking
                            minWidth: 400,
                        }}
                        gap={4}
                        ref={refContainer}
                    >
                        <Typography.Text>
                            {type === 'placement'
                                ? t('topLines.clickToSeePlacement')
                                : t('topLines.clickToSeePlatform')}
                            {hasSelected && (
                                <>
                                    {` - `}
                                    <Typography.Text strong>
                                        {type === 'placement'
                                            ? t('topLines.placementsSelected', {
                                                  count: Object.keys(
                                                      selected,
                                                  ).filter(key => selected[key])
                                                      .length,
                                              })
                                            : t('topLines.platformsSelected', {
                                                  count: Object.keys(
                                                      selected,
                                                  ).filter(key => selected[key])
                                                      .length,
                                              })}
                                    </Typography.Text>
                                </>
                            )}
                        </Typography.Text>
                        {list.map((item, index) => {
                            const isSelected = selected[item.id];

                            let url = null;

                            if (type === 'placement') {
                                url = getPlacementLogoUrl(item);
                            }

                            let icon: React.ReactNode = null;

                            if (type === 'platform') {
                                if (
                                    [
                                        'Instagram',
                                        'YouTube',
                                        'TikTok',
                                        'Twitter',
                                    ].includes(item.name)
                                ) {
                                    icon = (
                                        <i
                                            className={`fa-brands fa-${item.name.toLowerCase()}`}
                                        />
                                    );
                                }

                                if (['Facebook'].includes(item.name)) {
                                    icon = (
                                        <i
                                            className={`fa-brands fa-facebook-f`}
                                        />
                                    );
                                }

                                if (['X'].includes(item.name)) {
                                    icon = (
                                        <i
                                            style={{
                                                width: 14,
                                            }}
                                            className={`fa-brands fa-x-twitter`}
                                        />
                                    );
                                }

                                if (['Web'].includes(item.name)) {
                                    icon = (
                                        <i
                                            style={{
                                                width: 14,
                                            }}
                                            className={`fa-brands fa-browser`}
                                        />
                                    );
                                }

                                if (['App'].includes(item.name)) {
                                    icon = (
                                        <i
                                            style={{
                                                width: 14,
                                            }}
                                            className={`fa-brands fa-grid-2`}
                                        />
                                    );
                                }

                                if (['Broadcast'].includes(item.name)) {
                                    icon = (
                                        <img
                                            className="logo"
                                            style={{
                                                width: 14,
                                            }}
                                            src={liveTvLogo}
                                        />
                                    );
                                }

                                if (['In-Venue'].includes(item.name)) {
                                    icon = (
                                        <img
                                            className="logo"
                                            src={stadiumLogo}
                                        />
                                    );
                                }
                            }

                            return (
                                <Button
                                    key={item.id}
                                    style={{
                                        justifyContent: 'space-between',
                                        padding: '24px 16px',
                                        borderColor: 'transparent',
                                        borderRadius: 0,
                                        backgroundColor: isSelected
                                            ? 'rgba(186, 224, 255, 0.70)'
                                            : 'transparent',
                                    }}
                                    type={isSelected ? 'primary' : undefined}
                                    onClick={() => toggleOption(item.id)}
                                >
                                    <Flex>
                                        <Typography.Text
                                            style={{
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textAlign: 'left',
                                                marginRight: 16,
                                                width: 20,
                                            }}
                                        >
                                            {index + 1}.{` `}
                                        </Typography.Text>
                                        {(url || icon) && (
                                            <div
                                                style={{
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: 12,
                                                    overflow: 'hidden',
                                                    border:
                                                        type === 'placement'
                                                            ? `1px solid ${colorPrimary}`
                                                            : 'none',
                                                    marginRight: 16,
                                                    background:
                                                        type === 'platform'
                                                            ? colors10[index]
                                                            : undefined,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                }}
                                            >
                                                {url && (
                                                    <img
                                                        src={url}
                                                        height="100%"
                                                        width="100%"
                                                    />
                                                )}
                                                {icon}
                                            </div>
                                        )}

                                        <Typography.Text
                                            style={{
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                width: 200,
                                                textAlign: 'left',
                                            }}
                                        >
                                            {getPlacementDisplayName(item)}
                                        </Typography.Text>
                                    </Flex>
                                    <Tag
                                        color={colors10[index]}
                                        style={{
                                            width: 60,
                                            textAlign: 'center',
                                        }}
                                    >
                                        {format.shortCurrency(
                                            item.value,
                                            1,
                                            currency,
                                        )}
                                    </Tag>
                                </Button>
                            );
                        })}
                    </Flex>
                )}

                {/* Chart Container */}

                {!hasError && (
                    <Flex style={{ flex: 1, minWidth: 400 }}>
                        <div style={{ width: '100%' }}>
                            <Line {...config} />
                        </div>
                    </Flex>
                )}

                {!isLoadingGraph && !isLoadingTotal && hasError && (
                    <Flex
                        justify="center"
                        align="center"
                        style={{
                            flex: 1,
                            flexDirection: 'column',
                            gap: 8,
                            minHeight: 220,
                        }}
                    >
                        <i
                            className="fa-regular fa-circle-exclamation"
                            style={{ color: colorText, fontSize: 24 }}
                        />
                        <Typography.Text>
                            {t('overview.loadError')}
                        </Typography.Text>
                    </Flex>
                )}

                {!isLoadingGraph &&
                    !isLoadingTotal &&
                    !hasError &&
                    selectedData.length === 0 && (
                        <CenterMessage text={t('topLines.noDataAvailable')} />
                    )}
            </Flex>
        </Card>
    );
};

export default TopLines;
